import { useState, useCallback } from 'react';

import { QUESTION_TYPES } from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant.constants';
import type {
  GuidedPrompt,
  GuidedPromptAnswer,
  GuidedPromptAnswers,
} from 'containers/admin/clients/client/tabs-components/ai-assistant/helpers/guided-prompt-helpers';

interface UseGuidedPromptFormArgs {
  prompts: GuidedPrompt[];
  onSubmit: (answers: GuidedPromptAnswers) => void;
}

interface UseGuidedPromptFormResult {
  currentStep: number;
  currentPrompt: GuidedPrompt;
  totalSteps: number;
  isMultiStep: boolean;
  isLastStep: boolean;
  isMultiSelect: boolean;
  currentAnswer: GuidedPromptAnswer;
  canProceed: boolean;
  isFreeformActive: boolean;
  handleOptionSelect: (label: string) => void;
  handleFreeformChange: (text: string) => void;
  handleNext: () => void;
  handlePrevious: () => void;
  handleSkip: () => void;
}

const useGuidedPromptForm = ({
  prompts,
  onSubmit,
}: UseGuidedPromptFormArgs): UseGuidedPromptFormResult => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<GuidedPromptAnswers>({});

  const currentPrompt = prompts[currentStep];
  const totalSteps = prompts.length;
  const isMultiStep = totalSteps > 1;
  const isLastStep = currentStep === totalSteps - 1;
  const isMultiSelect =
    currentPrompt.questionType === QUESTION_TYPES.MULTI_SELECT;
  const currentAnswer: GuidedPromptAnswer = answers[currentStep] ?? {
    selectedOptions: [],
    freeformText: '',
  };
  const hasSelection =
    currentAnswer.selectedOptions.length > 0 ||
    currentAnswer.freeformText.trim().length > 0;
  const canProceed = isMultiStep
    ? !currentPrompt.required || hasSelection
    : hasSelection;
  const hasFreeformContent = currentAnswer.freeformText.trim().length > 0;
  const isFreeformActive =
    hasFreeformContent ||
    (!isMultiSelect &&
      currentAnswer.selectedOptions.length === 0 &&
      currentAnswer.freeformText !== '');

  const updateAnswer = useCallback(
    (
      stepIndex: number,
      updater: (previous: GuidedPromptAnswer) => GuidedPromptAnswer
    ) => {
      setAnswers((previousAnswers) => ({
        ...previousAnswers,
        [stepIndex]: updater(
          previousAnswers[stepIndex] ?? {
            selectedOptions: [],
            freeformText: '',
          }
        ),
      }));
    },
    []
  );

  const handleOptionSelect = useCallback(
    (label: string) => {
      updateAnswer(currentStep, (previous) => {
        if (isMultiSelect) {
          const alreadySelected = previous.selectedOptions.includes(label);

          return {
            ...previous,
            selectedOptions: alreadySelected
              ? previous.selectedOptions.filter((option) => option !== label)
              : [...previous.selectedOptions, label],
          };
        }

        return {
          selectedOptions: [label],
          freeformText: '',
        };
      });
    },
    [currentStep, isMultiSelect, updateAnswer]
  );

  const handleFreeformChange = useCallback(
    (text: string) => {
      updateAnswer(currentStep, (previous) => {
        if (isMultiSelect) {
          return { ...previous, freeformText: text };
        }

        return { selectedOptions: [], freeformText: text };
      });
    },
    [currentStep, isMultiSelect, updateAnswer]
  );

  const handleNext = useCallback(() => {
    if (isLastStep) {
      onSubmit(answers);
    } else {
      setCurrentStep((step) => step + 1);
    }
  }, [answers, isLastStep, onSubmit]);

  const handlePrevious = useCallback(() => {
    setCurrentStep((step) => Math.max(0, step - 1));
  }, []);

  const handleSkip = useCallback(() => {
    const { [currentStep]: _removed, ...remainingAnswers } = answers;

    setAnswers(remainingAnswers);

    if (isLastStep) {
      onSubmit(remainingAnswers);
    } else {
      setCurrentStep((step) => step + 1);
    }
  }, [answers, currentStep, isLastStep, onSubmit]);

  return {
    currentStep,
    currentPrompt,
    totalSteps,
    isMultiStep,
    isLastStep,
    isMultiSelect,
    currentAnswer,
    canProceed,
    isFreeformActive,
    handleOptionSelect,
    handleFreeformChange,
    handleNext,
    handlePrevious,
    handleSkip,
  };
};

export default useGuidedPromptForm;
